//
// Copyright 2022 Avvia Life, All Rights Reserved
//

import {
  useParams,
} from 'react-router-dom'

import { useTranslation } from 'react-i18next'

import {
  Avatar,
  Box,
  Divider,
} from '@mui/material'

import {
  useLangs,
  useLangCurrent,
} from '../../components/ALI18N'

import {
  rsIsLoading,
  rsIsError,
} from '../../remote-state/core'

import {
  capabilities,
} from '../../remote-state/authorization'

import {
  useCommunity,
  useCommunityReload,
  communityID,
  //communityAltID,
  communityName,
  communityNames,
  communityHead,
  communityHeadlines,
  communityDesc,
  communityDescriptions,
  communityAvatarID,
  communityArchived,
  communityImageID,
  communityLangs,

  communityGroup,
  communityGroupAuthzRoles,
  communityGroupAuthzRolesCountBits,
} from '../../remote-state/communities'

import {
  useImage,
  imageUrl,

  useImageLimits,
  imageLimitsRatio,
} from '../../remote-state/images'

import {
  ALMain,
} from '../../components/ALMain'

import {
  fieldRequired,
  fieldRequiredArray,
  useFieldStringMax,
  fieldChanged,
  ALFormTextField,
  ALFormImage,
} from '../../components/form/ALForm'

import {
  ALSettings,
} from '../../components/ALSettings'

import {
  ALEditor,
} from '../../components/ALDraftJS'

import ALLoading from '../../components/ALLoading'

//

export default function ALMainAdmSettings() {
  const { t, i18n } = useTranslation()
  const { cid } = useParams()

  const cmty = useCommunity ( cid )

  const { code } = useLangCurrent()
  const langs = useLangs()

  const communityReload = useCommunityReload( cid )

  const avatarID_L = communityAvatarID( cmty, 'light' )
  const avatarImg_L = useImage( avatarID_L )
  const avatarUrl_L = imageUrl( avatarImg_L )

  const avatarID_D = communityAvatarID( cmty, 'dark' )
  const avatarImg_D = useImage( avatarID_D )
  const avatarUrl_D = imageUrl( avatarImg_D )

  const imgID_L = communityImageID( cmty, 'light' )
  const imgUrl_L = imageUrl( useImage( imgID_L ) )

  const imgID_D = communityImageID( cmty, 'dark' )
  const imgUrl_D = imageUrl( useImage( imgID_D ) )

  const imageLimits = useImageLimits( 'banner' )

  const h = 2.5
  const imgHeight = h + 'rem'
  const imgWidth = ( h * imageLimitsRatio( imageLimits ) ) + 'rem'

  const values = {
    head : communityHead( cmty, [ code ], true ),
    headlines : communityHeadlines( cmty ),
    desc : communityDesc( cmty, [ code ], true ),
    descriptions : communityDescriptions( cmty ),
    languages : communityLangs( cmty ).map( lng => t( 'nativename', { lng } ) ).join(', '),
  }

  const ls = langs.map( elem => ( { value: elem.code, label : elem.nativeName } ) )
  const cls = communityLangs( cmty ).map( lng => ( { value: lng, label : t( 'nativename', { lng } ) } ) )

  const pOptions = t( 'rec.community.permission-levels-cmty', { returnObjects : true } )
  const jOptions = t( 'rec.community.join-options', { returnObjects: true } )
  const cOptions = t( 'rec.community.create-options', { returnObjects: true } )

  const group = communityGroup( cmty, cid )
  const group_site_cap = communityGroupAuthzRoles( group, 'site' )
  const group_member_cap = communityGroupAuthzRoles( group, 'member' )

  const dValue = communityGroupAuthzRolesCountBits( group, capabilities.index, [ 'public', 'site', 'member' ] ).toString()
  const d = pOptions.find( elem => elem.value === dValue ) ?? {}

  const iValue = communityGroupAuthzRolesCountBits( group, capabilities.read_info, [ 'public', 'site', 'member' ] ).toString()
  const i = pOptions.find( elem => elem.value === iValue ) ?? {}

  const rValue = communityGroupAuthzRolesCountBits( group, capabilities.read, [ 'public', 'site', 'member' ] ).toString()
  const r = pOptions.find( elem => elem.value === rValue ) ?? {}

  const jValue = ( group_site_cap & capabilities.join ) ? '3' : '0'
  const j = jOptions.find( elem => elem.value === jValue ) ?? {}

  const mcValue = ( group_member_cap & capabilities.create ) ? '2' : '0'
  const mc = cOptions.find( elem => elem.value === mcValue ) ?? {}

  const fieldMax80 = useFieldStringMax( 80 )
  const fieldMax2048 = useFieldStringMax( 2048 )

  if( rsIsLoading( cmty ) || rsIsLoading( imageLimits ) ) return ( <ALMain><ALLoading /></ALMain> )
  if( rsIsError( cmty ) ) return ( <ALMain>{ t( 'status.error.error') }</ALMain> )

  const onSuccess = () => { communityReload() }

  const settings = [
    {
      valueonly : true,
      label : t( 'rec.community.id' ),
      margin: 'none',
      value : communityID( cmty ),
      elems : [],
    },

    /*
    {
      label : t( 'rec.community.id_alt' ),
      margin: 'none',
      value : communityAltID( cmty ),
      service : 'api/communities',
      oid : communityID( cmty ),
      op : 'uid',
      onSuccess : onSuccess,
      elems : [
        { Comp: ALFormTextField, id: 'uid', value: communityAltID( cmty ), type: 'text', check: [ fieldRequired, fieldChanged ] },
      ],
    },
    */

    {
      valueonly : true,
      margin: 'none',
      value: ( <Divider/>),
      elems : [],
    },

    {
      label : t( 'rec.community.langs' ),
      margin: 'none',
      value : values.languages,
      service : 'api/communities',
      oid : communityID( cmty ),
      op : 'languages',
      onSuccess : onSuccess,
      elems : [
        { Comp: ALFormTextField, id: 'languages', name : t('rec.community.langs'), check: [ fieldRequiredArray, fieldChanged ], value: communityLangs( cmty ), select: true, SelectProps : { multiple: true }, options : [ { value: '', label: t( 'act.sellang' ), disabled : true }, ...ls ] },
      ],
    },

    {
      valueonly : true,
      margin: 'none',
      value: ( <Divider/>),
      elems : [],
    },

    {
      margin: 'none',
      label : t( 'rec.community.name' ),
      value : communityName( cmty, [ i18n.language ] ),
      service : 'api/communities',
      oid : communityID( cmty ),
      op : 'name',
      onSuccess : onSuccess,
      elems : [
        { Comp: ALFormTextField, id: 'language', local : true, name : t('rec.community.lang'), check: [ fieldRequired ], value: code, select: true, options : [ { value: '', label: t( 'act.sellang' ), disabled : true }, ...cls ] },
        { Comp: ALFormTextField, id: 'name', sub : 'language', value: communityNames( cmty ), type: 'text', check: [ fieldRequired, fieldChanged ] },
      ],
    },

    {
      label : t( 'rec.community.head' ),
      margin: 'none',
      value : values.head,
      service : 'api/communities',
      oid : communityID( cmty ),
      op : 'headline',
      onSuccess : onSuccess,
      elems : [
        { Comp: ALFormTextField, id: 'language', local : true, name : t('rec.community.lang'), check: [ fieldRequired ], value: code, select: true, options : [ { value: '', label: t( 'act.sellang' ), disabled : true }, ...cls ] },
        { Comp: ALFormTextField, id: 'headline', sub : 'language', value: values.headlines, type: 'text', minRows: 2, maxRows: 4, check: [ fieldChanged, fieldMax80 ] },
      ],
    },
    {
      label : t( 'rec.community.desc' ),
      margin: 'none',
      value : <ALEditor value={ values.desc } />,
      service : 'api/communities',
      oid : communityID( cmty ),
      op : 'description',
      onSuccess : onSuccess,
      elems : [
        { Comp: ALFormTextField, id: 'language', local : true, name : t('rec.community.lang'), check: [ fieldRequired ], value: code, select: true, options : [ { value: '', label: t( 'act.sellang' ), disabled : true }, ...cls ] },
        { Comp: ALFormTextField, id: 'description', sub : 'language', value: values.descriptions, minRows: 6, maxRows : 10, check : [ fieldChanged, fieldMax2048 ] },
      ],
    },

    {
      valueonly : true,
      margin: 'none',
      value: ( <Divider/>),
      elems : [],
    },

    {
      label : t( 'rec.community.avtr' ),
      margin: 'none',
      value : <Avatar variant='rounded' src={ avatarUrl_L } />,
      service : 'api/communities',
      oid : communityID( cmty ),
      op : 'avatar',
      onSuccess : onSuccess,
      elems : [
        { Comp: ALFormImage, id: 'avatar_id', value: avatarID_L, role: 'avatar', check : [ fieldChanged ] },
      ],
    },
    {
      label : t( 'rec.community.avtr_dm' ),
      margin: 'none',
      value : <Avatar variant='rounded' src={ avatarUrl_D } />,
      service : 'api/communities',
      oid : communityID( cmty ),
      op : 'avatar_dark',
      onSuccess : onSuccess,
      elems : [
        { Comp: ALFormImage, id: 'avatar_id', value: avatarID_D, role: 'avatar', check : [ fieldChanged ] },
      ],
    },

    {
      label : t( 'rec.community.bann' ),
      margin: 'none',
      value : <Avatar variant='rounded' src={ imgUrl_L } sx={{ height : imgHeight, width : imgWidth }} />,
      service : 'api/communities',
      oid : communityID( cmty ),
      op : 'image',
      onSuccess : onSuccess,
      elems : [
        { Comp: ALFormImage, id: 'image_id', value: imgID_L, role: 'banner', check : [ fieldChanged ] },
      ],
    },
    {
      label : t( 'rec.community.bann_dm' ),
      margin: 'none',
      value : <Avatar variant='rounded' src={ imgUrl_D } sx={{ height : imgHeight, width : imgWidth }} />,
      service : 'api/communities',
      oid : communityID( cmty ),
      op : 'image_dark',
      onSuccess : onSuccess,
      elems : [
        { Comp: ALFormImage, id: 'image_id', value: imgID_D, role: 'banner', check : [ fieldChanged ] },
      ],
    },

    {
      valueonly : true,
      margin: 'none',
      value: ( <Divider/>),
      elems : [],
    },

    {
      valueonly : true,
      margin: 'none',
      value: t( 'rec.community.permissions' ),
      elems : [],
    },

    {
      label : t( 'rec.community.index' ),
      margin: 'none',
      value : d.label,
      service : 'api/communities',
      oid : communityID( cmty ),
      op : 'permissions_search',
      onSuccess : onSuccess,
      elems : [
        { Comp: ALFormTextField, id: 'value', value: d.value,  select: true, check: [ fieldRequired, fieldChanged ], options : [ ...( pOptions.map( item => ({ value : item.value, label : item.label, disabled : item.disabled }) ) ) ] },
      ],
    },

    {
      label : <Box display='flex' alignItems='center' ><Box width='1rem' /><Box>{ t( 'rec.community.read' ) }</Box></Box>,
      margin: 'none',
      value : r.label,
      service : 'api/communities',
      oid : communityID( cmty ),
      op : 'permissions_read',
      onSuccess : onSuccess,
      elems : [
        { Comp: ALFormTextField, id: 'value', value: r.value,  select: true, check: [ fieldRequired, fieldChanged ], options : [ ...( pOptions.map( item => ({ value : item.value, label : item.label, disabled : item.disabled }) ) ) ] },
      ],
    },

    {
      label : <Box display='flex' alignItems='center' ><Box width='2rem' /><Box>{ t( 'rec.community.read-info' ) }</Box></Box>,
      margin: 'none',
      value : i.label,
      service : 'api/communities',
      oid : communityID( cmty ),
      op : 'permissions_read_info',
      onSuccess : onSuccess,
      elems : [
        { Comp: ALFormTextField, id: 'value', value: i.value,  select: true, check: [ fieldRequired, fieldChanged ], options : [ ...( pOptions.map( item => ({ value : item.value, label : item.label, disabled : item.disabled }) ) ) ] },
      ],
    },

    {
      label : t( 'rec.community.join' ),
      margin: 'none',
      value : j.label,
      service : 'api/communities',
      oid : communityID( cmty ),
      op : 'permissions_join',
      onSuccess : onSuccess,
      elems : [
        { Comp: ALFormTextField, id: 'value', value: j.value,  select: true, check: [ fieldRequired, fieldChanged ], options : [ ...( jOptions.map( item => ({ value : item.value, label : item.label, disabled : item.disabled }) ) ) ] },
      ],
    },

    {
      label : t( 'rec.community.member-create' ),
      margin: 'none',
      value : mc.label,
      service : 'api/communities',
      oid : communityID( cmty ),
      op : 'permissions_create_member',
      onSuccess : onSuccess,
      elems : [
        { Comp: ALFormTextField, id: 'value', value: mc.value,  select: true, check: [ fieldRequired, fieldChanged ], options : [ ...( cOptions.map( item => ({ value : item.value, label : item.label, disabled : item.disabled }) ) ) ] },
      ],
    },
  ]

  return(
    <ALMain title={`${ communityName( cmty, [ i18n.language ] ) } - Admin` } alerts={ communityArchived( cmty ) ? [ { severity : 'warning', message: t( `mod.community.archived_${ communityArchived( cmty ) }` ) } ] : null } >
      <ALSettings items={settings} />
    </ALMain>
  )
}